import { RefreshCw } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button, ErrorNote, Mono, PageHeader, Table, Td, Th } from '@/components/ui/primitives';
import { DomainStatus, StatusBadge } from '@/components/ui/status-badge';
import { formatTime } from '@/lib/api';
import { useSession } from '@/lib/session';
import { useSystemHealth } from './dashboard';

export function SystemHealthPage() {
  const { can } = useSession();
  const q = useSystemHealth(can('system.health.read'));

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageHeader
        title="System Health"
        description="Readiness of the API and the dependencies it needs, plus the latest backup and restore test."
        actions={
          <Button onClick={() => void q.refetch()}>
            <RefreshCw
              className={q.isFetching ? 'size-3.5 animate-spin' : 'size-3.5'}
              aria-hidden
            />
            Refresh
          </Button>
        }
      />
      <Card>
        <CardHeader className="flex items-center gap-3">
          <CardTitle>API service</CardTitle>
          <span className="ml-auto" data-testid="overall-status">
            <StatusBadge status={q.isPending ? 'unknown' : q.isError ? 'fail' : q.data.status} />
          </span>
        </CardHeader>
        <CardBody className="p-0">
          {q.isError ? (
            <div className="p-4">
              <ErrorNote error={q.error} />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Check</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Duration</Th>
                  <Th>Detail</Th>
                </tr>
              </thead>
              <tbody>
                {(q.data?.checks ?? []).map((c) => (
                  <tr key={c.name}>
                    <Td>
                      <Mono>{c.name}</Mono>
                    </Td>
                    <Td>
                      <StatusBadge status={c.status} />
                    </Td>
                    <Td className="text-right text-muted tabular-nums">
                      {c.durationMs.toFixed(1)} ms
                    </Td>
                    <Td className="text-muted">{c.message ?? '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Backup and restore</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          <Table>
            <thead>
              <tr>
                <Th>Kind</Th>
                <Th>Status</Th>
                <Th>Started</Th>
                <Th>Completed</Th>
              </tr>
            </thead>
            <tbody>
              {(q.data?.backups ?? []).map((b) => (
                <tr key={b.kind}>
                  <Td className="capitalize">{b.kind.replace('_', ' ')}</Td>
                  <Td>
                    <DomainStatus value={b.status} />
                  </Td>
                  <Td className="text-muted">{formatTime(b.startedAt)}</Td>
                  <Td className="text-muted">{formatTime(b.completedAt)}</Td>
                </tr>
              ))}
              {(q.data?.backups ?? []).length === 0 && (
                <tr>
                  <Td colSpan={4} className="text-muted">
                    No runs recorded yet.
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </div>
  );
}
